/**
 * The two-business-day clock on PM approvals (#496).
 *
 * Policy, layered over the calendar primitive in `utils/business-days.ts`. Kept apart from
 * it because "what is a working day" and "how long does a PM get" change for entirely
 * different reasons, and because everything here has to be importable by the badge, the
 * dashboard card, accounting's queue and the escalation digest alike.
 *
 * ── ONE MODULE, FOUR SURFACES ──
 *
 * The amber chip on the work-order row, the red "2 days overdue" on the dashboard, the
 * overdue count in the portal banner and the escalation list mailed to accounting must all
 * agree to the day. The inbox digest's scar is the precedent: a sweep that disagrees with
 * the screen it links to is worse than no sweep, because now two numbers are wrong and
 * nobody knows which.
 *
 * ── WHY THE CLOCK STARTS AT `review_approved_at` ──
 *
 * Not `completed_at`. The PM's window opens when the work order actually reaches their
 * queue — i.e. when accounting finishes the customer-facing rewrite — and a job that sat
 * in review for a week must not arrive already overdue. A send-back and re-mark therefore
 * RESTARTS the clock, since the round trip was the PM's own doing and charging them for it
 * would penalise the review they asked for.
 *
 * ── IN: REAL INSTANTS. OUT: BUSINESS DATES. DO NOT MIX THEM. ──
 *
 * Every `…AtMs` / `nowMs` parameter here is a REAL TIMESTAMP — `Date.now()`, or
 * `review_approved_at` straight out of D1 — and is normalised internally with `etDayMs`.
 * Every returned date is a business date (midnight-UTC marker), comparable with
 * `todayBusinessMs()`.
 *
 * Handing one of these functions a business date instead is a silent one-day error, and it
 * CANNOT be detected: at midnight UTC the two shapes are the same number. Midnight UTC is
 * 8 p.m. the previous evening in Eastern, so `etDayMs` resolves such an input to the day
 * BEFORE the one intended and every deadline lands a day early. Pinned by
 * "resolves a midnight-UTC marker to the previous Eastern day" in the spec — a mistake the
 * first draft of that spec made in seven places at once, which is why this warning is here
 * rather than left as folklore.
 */

import {
	addBusinessDays,
	businessDaysBetween,
	HolidayCoverageError,
	isHolidayCoverageKnown,
	WEEKENDS_ONLY,
} from './business-days';
import { etDayMs } from './date-utils';

/**
 * How long a property manager gets, in business days.
 *
 * Two. Named rather than inlined because it appears in the due-date math, the escalation
 * threshold and the copy of three separate emails, and those must not be able to drift.
 */
export const APPROVAL_SLA_BUSINESS_DAYS = 2;

/**
 * Warn once per process, not once per row.
 *
 * The degraded path below is reached from list renders and from a cron that walks every
 * pending work order, so an unguarded `console.warn` would emit thousands of identical
 * lines and bury the one thing worth reading.
 */
const warned = new Set<string>();
function warnOnce(key: string, message: string): void {
	if (warned.has(key)) return;
	warned.add(key);
	console.warn(message);
}

/**
 * Run `fn` against the real calendar, falling back to weekends-only if the date is outside
 * the generated holiday window.
 *
 * The fallback is deliberate and narrow. Being a day optimistic about a deadline in 2041 is
 * a far better outcome than a work-order page that throws a 500 because nobody re-ran the
 * generator — but it must never be SILENT, because the whole point of the generated
 * calendar is that its expiry announces itself.
 */
function withCalendar<T>(anchorMs: number, fn: (holidays?: ReadonlySet<string>) => T): T {
	if (isHolidayCoverageKnown(anchorMs)) {
		try {
			return fn();
		} catch (e) {
			if (!(e instanceof HolidayCoverageError)) throw e;
			// The anchor was in range but the walk stepped out of it (a late-December
			// anchor reaching into January of the first uncovered year).
		}
	}
	warnOnce(
		'approval-sla-coverage',
		'Approval SLA fell back to a weekends-only calendar: a date reached outside the generated '
		+ 'holiday window, so due dates may be a day early. Run `bun run holidays:generate`.',
	);
	return fn(WEEKENDS_ONLY);
}

/**
 * The last business day on which approving is still on time.
 *
 * "End of the second business day after review" — so marked ready Friday is due end of
 * Tuesday, and marked ready the Tuesday before Thanksgiving is due that Friday (or the
 * following Monday, once the day-after-Thanksgiving closure is added to
 * `SBS_EXTRA_CLOSURE_NAMES`).
 *
 * Whole-day granularity on purpose: it is the version a person can act on without doing
 * arithmetic ("you have until end of day Wednesday"), and an hours-based clock would make
 * a 4:55 p.m. hand-off arrive with three minutes of its first day left.
 *
 * @param reviewApprovedAtMs when accounting marked it ready — a real instant, not a
 *   business date; it is normalised to its ET calendar day here.
 * @returns a business date (midnight-UTC marker), comparable with `todayBusinessMs()`.
 */
export function approvalDueBusinessMs(reviewApprovedAtMs: number): number {
	const anchor = etDayMs(reviewApprovedAtMs);
	return withCalendar(anchor, (holidays) => addBusinessDays(anchor, APPROVAL_SLA_BUSINESS_DAYS, holidays));
}

/**
 * Business days a work order has been sitting in the PM's queue, counting from the day
 * after it arrived.
 *
 * Reaches `APPROVAL_SLA_BUSINESS_DAYS` on the day it is due — that day is still on time —
 * and exceeds it the morning after. Drives the "N days overdue" copy, which reads the
 * overage rather than this raw figure.
 */
export function businessDaysPending(reviewApprovedAtMs: number, nowMs: number): number {
	const anchor = etDayMs(reviewApprovedAtMs);
	const today = etDayMs(nowMs);
	return withCalendar(anchor, (holidays) => businessDaysBetween(anchor, today, holidays));
}

/**
 * Has the window closed?
 *
 * Compares CALENDAR DAYS, not the elapsed-business-day count, so the answer is stable for
 * the whole of the due date no matter when in it you ask. Today being the due date is on
 * time; tomorrow is not.
 */
export function isApprovalOverdue(reviewApprovedAtMs: number, nowMs: number): boolean {
	return etDayMs(nowMs) > approvalDueBusinessMs(reviewApprovedAtMs);
}

/**
 * Business days past due, or 0 when still inside the window.
 *
 * This is the number the UI and the escalation email should print. `businessDaysPending`
 * minus the allowance would give the same answer, but leaving that subtraction to four
 * call sites is how one of them ends up off by one.
 */
export function businessDaysOverdue(reviewApprovedAtMs: number, nowMs: number): number {
	const due = approvalDueBusinessMs(reviewApprovedAtMs);
	const today = etDayMs(nowMs);
	if (today <= due) return 0;
	return withCalendar(due, (holidays) => businessDaysBetween(due, today, holidays));
}

/**
 * Everything a row needs to render its own SLA state, in one pass.
 *
 * A list of 100 pending approvals otherwise calls three of the functions above per row,
 * each re-walking the same calendar. This also keeps the numbers in a single row
 * internally consistent — an overdue flag computed separately from the day count can
 * disagree at a midnight boundary.
 */
export interface ApprovalSlaStatus {
	/** Business date the approval is due (midnight-UTC marker). */
	dueMs: number;
	overdue: boolean;
	/** Business days past due; 0 when on time. */
	daysOverdue: number;
	/** Business days elapsed since it landed in the queue. */
	daysPending: number;
}

export function approvalSlaStatus(reviewApprovedAtMs: number, nowMs: number): ApprovalSlaStatus {
	const anchor = etDayMs(reviewApprovedAtMs);
	const today = etDayMs(nowMs);
	return withCalendar(anchor, (holidays) => {
		const dueMs = addBusinessDays(anchor, APPROVAL_SLA_BUSINESS_DAYS, holidays);
		const overdue = today > dueMs;
		return {
			dueMs,
			overdue,
			daysOverdue: overdue ? businessDaysBetween(dueMs, today, holidays) : 0,
			daysPending: businessDaysBetween(anchor, today, holidays),
		};
	});
}

/** "2 business days overdue" / "due today" / "due in 1 business day" — shared copy. */
export function approvalSlaLabel(status: ApprovalSlaStatus, nowMs: number): string {
	if (status.overdue) {
		const n = status.daysOverdue;
		return `${n} business day${n === 1 ? '' : 's'} overdue`;
	}
	const today = etDayMs(nowMs);
	if (today >= status.dueMs) return 'Due today';
	const remaining = businessDaysBetween(today, status.dueMs);
	return `Due in ${remaining} business day${remaining === 1 ? '' : 's'}`;
}
